/**
 * Admin → Edge Provisioning wizard
 *
 * Phase 7 item 2 (2026-05-16); platform picker added 2026-07-11. Flow:
 *   1. Pick component (discovery / activity / detection) + deployment
 *      platform (Kubernetes / Docker / Linux / Windows / macOS) + scope.
 *   2. Mint a short-TTL bootstrap token. Raw token is shown ONCE on this
 *      page; the controller stores only sha256(token).
 *   3. Operator downloads the generated per-platform installer(s) and runs
 *      them on the target. Within ~a minute the agent heartbeats in, the
 *      page flips to "Connected", and the pod appears on /admin/edge-fleet.
 *
 * Token lifetime semantics (2026-07-11): TTL gates ENROLLMENT only. Once
 * the agent enrolls (first heartbeat binds the token to its device id),
 * the same token keeps working for that device until the pod is
 * decommissioned - so standing agents (servers, laptops) never die at TTL.
 *
 * No state is persisted in the page beyond the wizard step - leaving
 * the page burns the only copy of the raw token. Mint again to get a
 * fresh one.
 *
 * Backend:
 *   POST /v1/edge/install-tokens
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import {
  Loader2,
  AlertTriangle,
  ShieldCheck,
  Clipboard,
  Check,
  ArrowRight,
  Server,
  Activity as ActivityIcon,
  ShieldAlert,
  Download,
  KeyRound,
  Wifi,
  WifiOff,
  Boxes,
  Container,
  Terminal,
  Laptop,
  Apple,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  mintEdgeInstallToken,
  listEdgeFleet,
  type EdgeComponent,
  type EdgeInstallArtifact,
  type EdgeInstallPlatform,
  type EdgePodSummary,
  type MintInstallTokenInput,
} from '@/lib/api';

type Step = 'configure' | 'minted';

/** First-heartbeat detection: how long to keep polling before giving up. */
const HEARTBEAT_WAIT_TIMEOUT_MS = 5 * 60 * 1000;
/** Polling cadence - 4s gives a snappy "Connected!" within ~one heartbeat cycle. */
const HEARTBEAT_POLL_INTERVAL_MS = 4_000;

const COMPONENT_OPTIONS: ReadonlyArray<{
  value: EdgeComponent;
  label: string;
  helper: string;
  icon: typeof Server;
}> = [
  { value: 'discovery', label: 'Discovery (DSPM)', helper: 'Scans data sources for sensitive data', icon: Server },
  { value: 'activity', label: 'Activity (DAM)', helper: 'Tails database activity logs', icon: ActivityIcon },
  { value: 'detection', label: 'Detection (DDR)', helper: 'Runs the Decision Gateway + OPA sidecar', icon: ShieldAlert },
];

/**
 * Deployment platforms (2026-07-11). Non-Kubernetes flavours run the Rust
 * discovery agent directly, so they are only offered for the discovery
 * component; activity/detection ship as containers (Kubernetes / Docker).
 */
const PLATFORM_OPTIONS: ReadonlyArray<{
  value: EdgeInstallPlatform;
  label: string;
  helper: string;
  icon: typeof Server;
  discoveryOnly: boolean;
  /** Shown while waiting for the first heartbeat. */
  runHint: string;
  /** Shown in the no-heartbeat troubleshooting card. */
  logsHint: string;
  /**
   * Set when no agent binary is published for this platform, which makes the
   * install impossible rather than merely unsupported. Shown to the operator
   * instead of handing them an installer that 404s at the download step.
   *
   * Disabled rather than removed on purpose: deleting the option would hide
   * that the platform is a real target, and someone would add it back without
   * knowing why it went. Remove this line when the binary ships.
   */
  unavailableReason?: string;
}> = [
  {
    value: 'kubernetes', label: 'Kubernetes', icon: Boxes, discoveryOnly: false,
    helper: 'Helm chart into your cluster (cloud / on-prem VPC)',
    runHint: './install.sh in a kubectl context with rights to the foctta-edge namespace',
    logsHint: 'kubectl -n foctta-edge logs -l app=foctta-edge --tail=200',
  },
  {
    value: 'docker', label: 'Docker', icon: Container, discoveryOnly: false,
    helper: 'docker-compose.yml for a single host',
    runHint: 'docker compose up -d next to the downloaded docker-compose.yml',
    logsHint: 'docker compose logs --tail 200',
  },
  {
    value: 'linux', label: 'Linux server', icon: Terminal, discoveryOnly: true,
    helper: 'systemd service + timer (file servers, app hosts)',
    runHint: 'sudo bash install-foctta-edge.sh on the target server',
    logsHint: 'journalctl -u foctta-edge-discovery -n 50',
  },
  {
    value: 'windows', label: 'Windows', icon: Laptop, discoveryOnly: true,
    helper: 'Scheduled Task (laptops, file servers)',
    runHint: 'powershell -ExecutionPolicy Bypass -File install-foctta-edge.ps1 on the target machine',
    logsHint: 'Task Scheduler > "FOCTTA Edge Discovery" > Last Run Result',
  },
  {
    value: 'macos', label: 'macOS', icon: Apple, discoveryOnly: true,
    // No macOS binary has ever been published: /downloads/foctta-endpoint-macos-*
    // returns 404. macOS cannot be cross-compiled without Apple hardware, so it
    // is built on demand if a customer actually has Macs.
    unavailableReason: 'No macOS agent binary is published yet - built on demand.',
    helper: 'launchd LaunchAgent (MacBooks)',
    runHint: 'bash install-foctta-edge.sh on the target Mac',
    logsHint: 'log show --last 10m --predicate \'process == "foctta-edge-discovery"\'',
  },
];

interface MintedToken {
  tokenId: string;
  bootstrapToken: string;
  expiresAt: string;
  component: EdgeComponent;
  platform: EdgeInstallPlatform;
  artifacts: EdgeInstallArtifact[];
  helmValues: string;
  installScript: string;
}

function downloadAsFile(name: string, contents: string): void {
  const blob = new Blob([contents], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function AdminEdgeProvisioning(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('configure');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Configure step state
  const [component, setComponent] = useState<EdgeComponent>('discovery');
  const [platform, setPlatform] = useState<EdgeInstallPlatform>('kubernetes');
  const [displayName, setDisplayName] = useState('Edge pod - apex/disc-01');
  const [ttlSeconds, setTtlSeconds] = useState(15 * 60);
  // 'fleet' is what makes one MDM package cover a rollout: N enrolments on one
  // token, capped by maxDevices and counted by the database. 'single' stays the
  // default so a token minted without thinking still cannot be replayed.
  const [kind, setKind] = useState<'single' | 'fleet'>('single');
  const [maxDevices, setMaxDevices] = useState(50);
  const [allowedIpCidr, setAllowedIpCidr] = useState('');

  // Non-k8s/docker platforms run the Rust discovery agent directly - keep
  // the picker consistent when the operator switches to activity/detection.
  function pickComponent(next: EdgeComponent): void {
    setComponent(next);
    if (next !== 'discovery') {
      const opt = PLATFORM_OPTIONS.find((p) => p.value === platform);
      if (opt?.discoveryOnly) setPlatform('kubernetes');
    }
  }

  // Minted step state
  const [minted, setMinted] = useState<MintedToken | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [mintedAt, setMintedAt] = useState<number | null>(null);

  // First-heartbeat wait state - once a pod row appears that matches the
  // displayName the operator gave us, we stop polling and surface the
  // "Connected!" CTA. Until then we show a live "Waiting for heartbeat"
  // indicator so the operator knows the page is doing something while
  // they `helm install` in another terminal.
  const [connectedPod, setConnectedPod] = useState<EdgePodSummary | null>(null);
  const [waitTimedOut, setWaitTimedOut] = useState(false);

  async function handleMint(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const input: MintInstallTokenInput = {
        component,
        platform,
        displayName: displayName.trim(),
        ttlSeconds,
      };
      if (allowedIpCidr.trim()) input.allowedIpCidr = allowedIpCidr.trim();
      if (kind === 'fleet') {
        input.kind = 'fleet';
        input.maxDevices = maxDevices;
      }
      const r = await mintEdgeInstallToken(input);
      setMinted(r);
      setMintedAt(Date.now());
      setConnectedPod(null);
      setWaitTimedOut(false);
      setStep('minted');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to mint install token');
    } finally {
      setBusy(false);
    }
  }

  // First-heartbeat polling - runs while we're in the minted step + haven't
  // matched a pod yet + the wait window hasn't expired. EdgePodSummary
  // doesn't carry the install-token id, so we match on (component + display
  // name + createdAt >= mintedAt). That's the same tuple the install script
  // propagates into values.yaml, so it's a reliable identity for one mint.
  useEffect(() => {
    if (step !== 'minted' || !minted || mintedAt === null) return;
    if (connectedPod || waitTimedOut) return;

    let cancelled = false;
    const startedAt = mintedAt;
    // The mint response doesn't echo displayName, so fall back to the
    // operator-entered local state (the install script propagates this
    // exact string into the pod's bootstrap config, so the match is sound).
    const target = displayName.trim().toLowerCase();
    const wantedComponent = minted.component;

    async function pollOnce(): Promise<void> {
      try {
        const res = await listEdgeFleet({ component: wantedComponent, limit: 100 });
        if (cancelled) return;
        // Match: same component, same displayName (case-insensitive), and
        // EITHER created after the mint OR heartbeating after the mint
        // (a re-enrolling device updates its EXISTING fleet row, so a fresh
        // heartbeat is as good as a fresh row). 30s grace for clock skew.
        const match = res.pods.find((p) => {
          if (p.component !== wantedComponent) return false;
          if ((p.displayName ?? '').trim().toLowerCase() !== target) return false;
          const createdMs = new Date(p.createdAt).getTime();
          if (Number.isFinite(createdMs) && createdMs >= startedAt - 30_000) return true;
          const beatMs = p.lastHeartbeatAt ? new Date(p.lastHeartbeatAt).getTime() : NaN;
          return Number.isFinite(beatMs) && beatMs >= startedAt - 30_000;
        });
        if (match) {
          setConnectedPod(match);
          return;
        }
      } catch {
        // Transient errors during polling are non-fatal - keep trying until
        // we time out. The user already has the bootstrap token; the wait
        // is best-effort.
      }
      if (Date.now() - startedAt > HEARTBEAT_WAIT_TIMEOUT_MS) {
        if (!cancelled) setWaitTimedOut(true);
        return;
      }
    }

    void pollOnce();
    const iv = window.setInterval(() => void pollOnce(), HEARTBEAT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
    };
  }, [step, minted, mintedAt, connectedPod, waitTimedOut]);

  async function copy(key: string, value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // Older browsers without permission - fall through silently.
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Provision Edge agent"
        subtitle={
          step === 'configure'
            ? 'Pick component + where it deploys, mint a bootstrap token, then run the generated installer on the target - the page flips to Connected on the first heartbeat.'
            : 'Token minted. The raw token below is the ONLY copy - leaving this page invalidates it from your side (the controller keeps sha256 only).'
        }
        badges={[
          { label: step === 'configure' ? 'Step 1 of 2' : 'Step 2 of 2', variant: 'glass' },
          { label: 'Short enrollment window · device-bound after first heartbeat', variant: 'glass' },
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => navigate({ to: '/admin/edge-fleet' })}>
            ← Back to Fleet
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Mint failed"
          detail={error}
        />
      )}

      {step === 'configure' && (
        <div className="space-y-4 max-w-2xl">
          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-3">
              <CardTitle>1. Pick component</CardTitle>
              <CardDescription>Which Edge agent are you installing?</CardDescription>
            </CardHeader>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {COMPONENT_OPTIONS.map((o) => {
                const Icon = o.icon;
                const selected = component === o.value;
                return (
                  <motion.button
                    key={o.value}
                    type="button"
                    onClick={() => pickComponent(o.value)}
                    initial={false}
                    animate={{ scale: selected ? 1.02 : 1 }}
                    className={`text-left p-3 rounded-md border transition-colors ${
                      selected
                        ? 'border-primary bg-primary/10'
                        : 'border-border/30 glass-lite hover:bg-muted/30'
                    }`}
                  >
                    <Icon className="h-4 w-4 mb-2 text-primary" />
                    <div className="text-sm font-medium">{o.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{o.helper}</div>
                  </motion.button>
                );
              })}
            </div>
          </GlassCard>

          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-3">
              <CardTitle>2. Where does it deploy?</CardTitle>
              <CardDescription>
                The wizard generates a ready-to-run installer for the platform you pick.
              </CardDescription>
            </CardHeader>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {PLATFORM_OPTIONS.map((o) => {
                const Icon = o.icon;
                const selected = platform === o.value;
                const disabled =
                  (o.discoveryOnly && component !== 'discovery') || !!o.unavailableReason;
                return (
                  <motion.button
                    key={o.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => setPlatform(o.value)}
                    initial={false}
                    animate={{ scale: selected ? 1.02 : 1 }}
                    title={
                      o.unavailableReason
                        ? o.unavailableReason
                        : disabled
                          ? 'Available for the Discovery agent only'
                          : undefined
                    }
                    className={`text-left p-3 rounded-md border transition-colors ${
                      disabled
                        ? 'border-border/20 opacity-40 cursor-not-allowed'
                        : selected
                          ? 'border-primary bg-primary/10'
                          : 'border-border/30 glass-lite hover:bg-muted/30'
                    }`}
                  >
                    <Icon className="h-4 w-4 mb-2 text-primary" />
                    <div className="text-sm font-medium">{o.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{o.helper}</div>
                    {/*
                      Shown, not just tooltipped. A greyed tile with the reason
                      hidden behind a hover is a dead end on touch, and "why is
                      this one off?" becomes a support question instead of an
                      answer the operator already has.
                    */}
                    {o.unavailableReason && (
                      <div className="text-[10px] text-amber-400 mt-1">{o.unavailableReason}</div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </GlassCard>

          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-3">
              <CardTitle>3. Device label + scope</CardTitle>
              <CardDescription>
                Names the agent in the fleet (and, for server/laptop installs, becomes the device id) + optional IP pin.
              </CardDescription>
            </CardHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Display name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Edge pod - apex/disc-01"
                  maxLength={200}
                  className="w-full glass-lite text-sm rounded px-3 py-2 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Token TTL</label>
                  <select
                    value={ttlSeconds}
                    onChange={(e) => setTtlSeconds(Number(e.target.value))}
                    className="w-full glass-lite text-sm rounded px-3 py-2 bg-background/40 border border-border/40"
                  >
                    <option value={5 * 60}>5 minutes</option>
                    <option value={15 * 60}>15 minutes (default)</option>
                    <option value={60 * 60}>1 hour</option>
                    <option value={4 * 60 * 60}>4 hours</option>
                    <option value={24 * 60 * 60}>24 hours</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">IP CIDR pin (optional)</label>
                  <input
                    type="text"
                    value={allowedIpCidr}
                    onChange={(e) => setAllowedIpCidr(e.target.value)}
                    placeholder="10.0.0.0/24"
                    maxLength={50}
                    className="w-full glass-lite text-sm rounded px-3 py-2 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Rollout</label>
                  <select
                    value={kind}
                    onChange={(e) => setKind(e.target.value as 'single' | 'fleet')}
                    className="w-full glass-lite text-sm rounded px-3 py-2 bg-background/40 border border-border/40"
                  >
                    <option value="single">One device (default)</option>
                    <option value="fleet">A fleet - one installer, many devices</option>
                  </select>
                </div>
                {kind === 'fleet' && (
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Device limit</label>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={maxDevices}
                      onChange={(e) => setMaxDevices(Number(e.target.value))}
                      className="w-full glass-lite text-sm rounded px-3 py-2 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                    />
                  </div>
                )}
              </div>
              {kind === 'fleet' && (
                <div className="text-[11px] text-muted-foreground glass-lite rounded p-2">
                  One installer enrols up to {maxDevices} devices. The count is
                  held by the database, so devices arriving at the same moment
                  cannot push it past the limit, and a device that re-enrols is
                  not counted twice. Pick a TTL that covers the rollout: the
                  window governs joining, and a device that has already joined
                  keeps working after it closes.
                </div>
              )}
              <div className="text-[11px] text-muted-foreground glass-lite rounded p-2">
                Defence-in-depth: the TTL is the ENROLLMENT window - the installer must run before it
                lapses. On first heartbeat the token binds to this device and keeps working for it
                until the pod is decommissioned (which revokes the token). Pin to a CIDR if the
                target has a static egress range - the controller refuses heartbeats from outside it.
              </div>
            </div>
          </GlassCard>

          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => void handleMint()}
              disabled={busy || displayName.trim().length === 0}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <KeyRound className="h-3.5 w-3.5 mr-1" />}
              Mint install token
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {step === 'minted' && minted && (
        <div className="space-y-4 max-w-3xl">
          <GlassCard className="p-5 border border-success/40">
            <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-success" />
                  Bootstrap token minted
                </CardTitle>
                <CardDescription>
                  Component: <strong>{minted.component}</strong> · Platform: <strong>{minted.platform}</strong> · Enroll before {new Date(minted.expiresAt).toLocaleString()}
                </CardDescription>
              </div>
              <Badge variant="success" className="text-[10px]">device-bound after enroll</Badge>
            </CardHeader>
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">Bootstrap token (shown once)</div>
              <div className="glass-lite rounded p-3 flex items-center gap-2">
                <code className="font-mono text-[11px] flex-1 break-all">{minted.bootstrapToken}</code>
                <Button
                  size="sm"
                  variant="glass"
                  onClick={() => void copy('token', minted.bootstrapToken)}
                  title="Copy token"
                >
                  {copiedKey === 'token' ? <Check className="h-3.5 w-3.5" /> : <Clipboard className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <div className="text-[11px] text-warning flex items-start gap-1.5 mt-2">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>Leaving this page burns your only copy. The controller stores SHA-256(token) only.</span>
              </div>
            </div>
          </GlassCard>

          {minted.artifacts.map((artifact) => (
            <GlassCard key={artifact.filename} className="p-5">
              <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between">
                <div>
                  <CardTitle>{artifact.filename}</CardTitle>
                  <CardDescription>{artifact.runNote}</CardDescription>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="glass" onClick={() => void copy(artifact.filename, artifact.contents)}>
                    {copiedKey === artifact.filename ? <Check className="h-3.5 w-3.5 mr-1" /> : <Clipboard className="h-3.5 w-3.5 mr-1" />}
                    Copy
                  </Button>
                  <Button size="sm" variant="glass" onClick={() => downloadAsFile(artifact.filename, artifact.contents)}>
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Download
                  </Button>
                </div>
              </CardHeader>
              <pre className="glass-lite rounded p-3 text-[11px] font-mono overflow-x-auto whitespace-pre max-h-80 overflow-y-auto">
                {artifact.contents}
              </pre>
            </GlassCard>
          ))}

          {/* First-heartbeat wait - replaces the static "Next step" hint with
              a live indicator that polls /v1/edge/fleet for the matching pod. */}
          {connectedPod ? (
            <div className="glass-lite rounded-md p-4 border border-success/40 flex items-start gap-3">
              <Wifi className="h-5 w-5 text-success shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-sm flex items-center gap-2">
                  Pod connected
                  <Badge variant="success" className="text-[9px]">{connectedPod.healthStatus}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  <code className="font-mono">{connectedPod.podId}</code>
                  {connectedPod.version ? ` · v${connectedPod.version}` : ''}
                  {connectedPod.lastHeartbeatAt
                    ? ` · last heartbeat ${new Date(connectedPod.lastHeartbeatAt).toLocaleTimeString()}`
                    : ''}
                </div>
              </div>
              <Button size="sm" onClick={() => navigate({ to: '/admin/edge-fleet' })}>
                Open in Fleet <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          ) : waitTimedOut ? (
            <div className="glass-lite rounded-md p-4 border border-warning/40 flex items-start gap-3">
              <WifiOff className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-sm">No heartbeat after 5 minutes</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Common causes: the installer hasn&rsquo;t been run yet, the target can&rsquo;t
                  reach the controller, or the allowed-CIDR pin excludes the target&rsquo;s NAT IP.
                  Check the agent logs:
                  <code className="font-mono block mt-1">
                    {PLATFORM_OPTIONS.find((p) => p.value === minted.platform)?.logsHint}
                  </code>
                </div>
              </div>
              <Button
                size="sm"
                variant="glass"
                onClick={() => {
                  setWaitTimedOut(false);
                  setMintedAt(Date.now());
                }}
              >
                Keep waiting
              </Button>
            </div>
          ) : (
            <div className="glass-lite rounded-md p-4 border border-primary/30 flex items-start gap-3">
              <Loader2 className="h-5 w-5 text-primary shrink-0 mt-0.5 animate-spin" />
              <div className="flex-1">
                <div className="font-medium text-sm">Waiting for agent heartbeat…</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Run: <code className="font-mono">
                    {PLATFORM_OPTIONS.find((p) => p.value === minted.platform)?.runHint}
                  </code>
                  . We poll the fleet every {HEARTBEAT_POLL_INTERVAL_MS / 1000}s and switch to
                  &ldquo;Connected&rdquo; the moment the agent heartbeats in (usually within ~a minute).
                </div>
              </div>
              <Button size="sm" variant="glass" onClick={() => navigate({ to: '/admin/edge-fleet' })}>
                Skip to Fleet
              </Button>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
}
